import type { AccountOrganization } from '@crow/ui';
import type { AppBootstrap } from '@crowdex/agent-contracts/api';
import { createContext, type ReactNode, useContext } from 'react';

/**
 * The visitor's Crowlingo account, when Crowdex is signed in through it (the cloud, Zitadel): its
 * organisation and role there. Null for a server on this computer, signed in by its launch code,
 * and for the preview: their account menu stays Crowdex's own.
 */
export type CrowlingoAccount = { organization: AccountOrganization | null };

const CrowlingoAccountContext = createContext<CrowlingoAccount | null>(null);

const ROLE_LABELS = { admin: 'Administrateur', member: 'Membre' } as const;

/** The Crowlingo account a bootstrap names: the cloud's only. */
export function crowlingoAccountOf(bootstrap: AppBootstrap): CrowlingoAccount | null {
  if (bootstrap.profile !== 'cloud') return null;
  const organization = bootstrap.organization;
  return {
    organization:
      organization == null
        ? null
        : { name: organization.name, role: ROLE_LABELS[organization.role] },
  };
}

export function CrowlingoAccountProvider({
  account,
  children,
}: {
  account: CrowlingoAccount | null;
  children: ReactNode;
}) {
  return (
    <CrowlingoAccountContext.Provider value={account}>{children}</CrowlingoAccountContext.Provider>
  );
}

export function useCrowlingoAccount(): CrowlingoAccount | null {
  return useContext(CrowlingoAccountContext);
}
